import { useEffect, useState } from 'react';
import type { Extension } from '@arcnet/contracts';
import { useSession } from '../../lib/auth/session.js';
import { useNumbers } from '../../lib/api/hooks-tms.js';
import { useTeams, useTeamsMutations, useTeamsStatus } from '../../lib/api/hooks-teams.js';
import { TeamsPresenceBadge, teamsProblems } from './TeamsPresence.js';
import { useExtensionFeature } from '../../lib/api/hooks-extension.js';
import { usePlatformFeatures } from '../../lib/api/hooks-features.js';
import { ApiClientError } from '../../lib/api/client.js';
import { Badge, CardHead, ErrorBanner, Segmented } from '../../components/ui/Primitives.js';
import { Switch } from '../../components/ui/Form.js';

/**
 * This extension in Microsoft Teams (docs/44; owner, 19 October 2026: "the individual extension
 * configs and enablement should be a feature gated on the extension").
 *
 * The account's connection -- its subdomain, verification and script -- is the Teams tab's. Here is
 * the seat's own part: whether it rings in Teams, the number its person has there, and the Microsoft
 * 365 sign-in whose Teams rings. Offered only when the extension includes the Teams feature (0166);
 * without it, a line says where to add it, as the ArcSIP app card does.
 *
 * The number is one of the account's own, never typed: the extension's DID (the one already routed
 * to it is chosen for you), or a shared number with the extension after it (+27215550100;ext=1001),
 * which Microsoft accepts for people without a DID of their own.
 */
export function ExtensionTeams({ tenantId, ext }: { tenantId: string; ext: Extension }) {
  const platform = usePlatformFeatures().data;
  const feature = useExtensionFeature(tenantId, ext.id, 'teams');
  const can = useSession((s) => s.can);
  const mayPackage = can('extension:package:write');
  // The platform offers Teams at all, and this person may see it.
  if (platform?.teams !== true || !can('teams:read')) return null;
  if (feature === undefined) return null;
  if (!feature) {
    return (
      <div className="arc-card" style={{ marginBottom: 14 }}>
        <CardHead title="Microsoft Teams" />
        <div className="arc-card-body arc-muted" style={{ fontSize: 13 }}>
          Microsoft Teams is not included in this extension's type
          {ext.extension_type_name ? ` (${ext.extension_type_name})` : ''}.
          {mayPackage ? ' Add it on the Features tab.' : ' Ask your provider to add it.'}
        </div>
      </div>
    );
  }
  return <TeamsSeatCard tenantId={tenantId} ext={ext} mayEdit={can('teams:write')} />;
}

function TeamsSeatCard({ tenantId, ext, mayEdit }: { tenantId: string; ext: Extension; mayEdit: boolean }) {
  const teams = useTeams(tenantId);
  const m = useTeamsMutations(tenantId);
  const status = useTeamsStatus(tenantId, !!teams.data?.seats.some((s) => s.extension_id === ext.id)).data;
  const live = status?.seats.find((s) => s.extension_id === ext.id);
  const problems = live ? teamsProblems(live, !!status?.microsoft_connected) : [];
  const numbers = useNumbers(tenantId).data?.data ?? [];
  const d = teams.data;
  const seat = d?.seats.find((s) => s.extension_id === ext.id);

  const [mode, setMode] = useState<'own' | 'shared'>('own');
  const [num, setNum] = useState('');
  const [who, setWho] = useState('');
  const [on, setOn] = useState(true);

  // What is saved, once it has loaded; otherwise the DID already routed to this extension.
  useEffect(() => {
    if (!d) return;
    if (seat) {
      const [base, extPart] = seat.teams_number.split(';ext=');
      setMode(extPart ? 'shared' : 'own');
      setNum(base ?? '');
      setWho(seat.teams_user ?? '');
      setOn(seat.enabled);
    } else {
      const routed = numbers.find((n) => n.route?.destination.type === 'extension' && n.route.destination.id === ext.id);
      setMode('own');
      setNum(routed && !d.seats.some((s) => s.teams_number === routed.e164) ? routed.e164 : '');
    }
    // Reset when the saved seat changes, not on every keystroke.
  }, [d, seat?.teams_number, seat?.teams_user, seat?.enabled, numbers.length]);

  if (teams.error) return <ErrorBanner error={teams.error} />;
  if (!d) return null;

  const err = m.setSeat.error instanceof ApiClientError ? m.setSeat.error : null;
  // A DID is one person's in Teams; a shared number can carry many extensions.
  const taken = new Set(d.seats.filter((s) => s.extension_id !== ext.id).map((s) => s.teams_number));
  const choices = mode === 'own' ? numbers.filter((n) => !taken.has(n.e164)) : numbers;
  const teamsNumber = !num ? '' : mode === 'own' ? num : `${num};ext=${ext.number}`;
  const dirty = !seat || seat.teams_number !== teamsNumber || (seat.teams_user ?? '') !== who.trim() || seat.enabled !== on;

  return (
    <div className="arc-card" style={{ marginBottom: 14 }}>
      <CardHead title="Microsoft Teams"
        sub={d.connection
          ? `Rings in Teams as well as on its phones, and calls made from Teams are this extension's. Through ${d.connection.fqdn}.`
          : 'This account is not connected to Microsoft Teams yet. Set it up on the account\'s Microsoft Teams tab first.'} />
      {seat && live && (
        <div className="arc-card-body arc-stack" style={{ gap: 6, borderBottom: '1px solid var(--arc-border)' }}>
          <div className="arc-row" style={{ gap: 8 }}>
            {status?.microsoft_connected ? <TeamsPresenceBadge seat={live} /> : <Badge tone="info">Teams</Badge>}
            {problems.length === 0
              ? <span className="arc-muted" style={{ fontSize: 13 }}>Calls to this extension ring this person in Teams.</span>
              : null}
          </div>
          {problems.map((p) => <span key={p} className="arc-hint" style={{ margin: 0 }}>{p}</span>)}
          {!status?.microsoft_connected && (
            <span className="arc-hint" style={{ margin: 0 }}>Connect Microsoft 365 on the account&apos;s Microsoft Teams page to see this person&apos;s Teams status.</span>
          )}
        </div>
      )}
      {d.connection && (
        <form className="arc-card-body arc-stack" style={{ gap: 14 }}
          onSubmit={(e) => {
            e.preventDefault();
            if (teamsNumber) {
              m.setSeat.mutate({ extensionId: ext.id, body: { teams_number: teamsNumber, teams_user: who.trim() || null, enabled: on } });
            }
          }}>
          <div className="arc-row" style={{ gap: 10, alignItems: 'center' }}>
            <Switch id={`teams-on-${ext.id}`} checked={on} disabled={!mayEdit} label="Rings in Teams" onChange={setOn} />
            <span>Rings in Teams</span>
          </div>
          <div className="arc-field" style={{ margin: 0 }}>
            <span className="arc-label">Number in Teams</span>
            <Segmented label="Number in Teams" value={mode} onChange={(v) => { if (mayEdit) { setMode(v); setNum(''); } }}
              options={[['own', 'Its own number'], ['shared', 'Shared number + extension']] as const} />
          </div>
          <div className="arc-row" style={{ gap: 14, flexWrap: 'wrap', alignItems: 'flex-start' }}>
            <label className="arc-field" style={{ margin: 0, minWidth: 220, flex: '1 1 260px' }}>
              <span className="arc-label">{mode === 'own' ? 'Its number' : 'Shared number'}</span>
              <select className="arc-select" value={num} disabled={!mayEdit} onChange={(e) => setNum(e.target.value)}>
                <option value="">{mode === 'own' ? 'Choose its number' : 'Choose the shared number'}</option>
                {choices.map((n) => <option key={n.id} value={n.e164}>{n.e164}{n.label ? ` · ${n.label}` : ''}</option>)}
              </select>
              {numbers.length === 0 && <p className="arc-hint">This account has no numbers yet. Assign one in DID &amp; Routing first.</p>}
              {mode === 'shared' && teamsNumber && <p className="arc-hint">In Teams: <span className="arc-mono">{teamsNumber}</span></p>}
              {err?.fieldErrors?.['teams_number'] && <div className="arc-error">{err.fieldErrors['teams_number']}</div>}
            </label>
            <label className="arc-field" style={{ margin: 0, minWidth: 220, flex: '1 1 260px' }}>
              <span className="arc-label">Teams sign-in</span>
              <input className="arc-input" type="email" value={who} disabled={!mayEdit} onChange={(e) => setWho(e.target.value)}
                placeholder="tim@contoso.com" autoComplete="off" spellCheck={false} />
              <p className="arc-hint">The Microsoft 365 email this person signs in to Teams with. The setup script uses it.</p>
              {err?.fieldErrors?.['teams_user'] && <div className="arc-error">{err.fieldErrors['teams_user']}</div>}
            </label>
          </div>
          {m.setSeat.error && !err?.fieldErrors?.['teams_number'] && !err?.fieldErrors?.['teams_user'] && <ErrorBanner error={m.setSeat.error} />}
          {mayEdit && (
            <div className="arc-row" style={{ gap: 8 }}>
              <button type="submit" className="arc-btn arc-btn-primary" disabled={!teamsNumber || !dirty || m.setSeat.isPending}>
                {m.setSeat.isPending ? 'Saving…' : 'Save'}
              </button>
              {seat && (
                <button type="button" className="arc-btn" disabled={m.removeSeat.isPending}
                  onClick={() => m.removeSeat.mutate(ext.id)}>
                  Take out of Teams
                </button>
              )}
            </div>
          )}
          {m.removeSeat.error && <ErrorBanner error={m.removeSeat.error} />}
        </form>
      )}
    </div>
  );
}
